import { useMemo, useRef, useState } from "react";
import { ArrowRight, Check, Plus, X } from "lucide-react";
import { useProduct } from "@/components/pdp/PdpDataContext";
import VariantPill from "@/components/pdp/VariantPill";
import { ROUTINE_COPY as C, ROUTINE_SLOTS } from "@/data/pdp-3-4/routine";

// PDP 3.4 — Build Your Routine + Save 10% (Shopify: sections/pdp34-bundle-builder.liquid).
// 4 fixed category slots (left) ↔ 4 category tabs (right). One product per slot;
// picking another product in the same category swaps it. 10% off at 4/4.

const parsePrice = (s) => parseFloat(String(s).replace(/[^0-9.]/g, "")) || 0;
const pickVariant = (p, label) =>
  p.variants?.find((v) => v.label === label) || p.variants?.find((v) => v.default) || p.variants?.[0];

const SlotTile = ({ slot, pick, active, onOpen, onRemove }) => {
  const filled = !!pick;
  return (
    <div className="flex flex-col" data-testid={`routine-slot-${slot.id}`} data-filled={filled}>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${slot.title}: ${filled ? "change product" : C.emptySlot}`}
        data-testid={`routine-slot-tile-${slot.id}`}
        className={`group relative aspect-square w-full overflow-hidden bg-[#F4F5F6] transition-shadow ${
          active ? "ring-1 ring-riversol-blue" : "ring-0 hover:ring-1 hover:ring-neutral-300"
        }`}
        style={{ borderRadius: "5px" }}
      >
        {filled ? (
          <img src={pick.product.image} alt={pick.product.name} className="absolute inset-0 w-full h-full object-cover bg-white" />
        ) : (
          <img src={slot.placeholder} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain p-7 opacity-80" />
        )}
        {filled && (
          <span className="absolute top-2 left-2 w-5 h-5 rounded-full bg-riversol-blue text-white flex items-center justify-center">
            <Check size={11} strokeWidth={3} />
          </span>
        )}
      </button>
      <div className="mt-3.5 min-h-[58px]">
        <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-neutral-800">
          {slot.n}. {slot.title}
        </div>
        {filled ? (
          <>
            <div className="text-[13px] text-neutral-900 leading-snug mt-1 line-clamp-1" data-testid={`routine-slot-name-${slot.id}`}>
              {pick.product.name}
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="font-mono text-[12px] text-neutral-600 tabular-nums">
                {pick.variant ? `${pick.variant.label} · ` : ""}{pick.price}
              </span>
              <span className="flex items-center gap-3">
                <button type="button" onClick={onOpen} className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-700 underline underline-offset-4 decoration-neutral-400 hover:text-riversol-blue hover:decoration-riversol-blue" data-testid={`routine-slot-change-${slot.id}`}>
                  Change
                </button>
                <button type="button" onClick={onRemove} aria-label={`Remove ${pick.product.name}`} className="text-neutral-400 hover:text-neutral-800" data-testid={`routine-slot-remove-${slot.id}`}>
                  <X size={13} strokeWidth={2} />
                </button>
              </span>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={onOpen}
            className="mt-2 w-full inline-flex items-center justify-between gap-2 border border-riversol-blue text-riversol-blue px-3.5 py-2.5 font-mono text-[11px] tracking-[0.12em] uppercase font-medium hover:bg-riversol-blue/[0.06] transition-colors"
            style={{ borderRadius: "5px" }}
            data-testid={`routine-slot-select-${slot.id}`}
          >
            {C.emptySlot}
            <ArrowRight size={14} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
};

const RoutinePanel = ({ picks, activeSlot, onOpen, onRemove }) => {
  const chosen = ROUTINE_SLOTS.map((s) => picks[s.id]).filter(Boolean);
  const count = chosen.length;
  const complete = count === ROUTINE_SLOTS.length;
  const subtotal = chosen.reduce((s, p) => s + parsePrice(p.price), 0);
  const total = complete ? subtotal * (1 - C.discountPct) : subtotal;
  return (
    <div className="bg-white border border-neutral-200 p-6 lg:p-8" data-testid="custom-set-panel">
      <h3 className="text-3xl lg:text-4xl tracking-tight font-semibold text-neutral-900 mb-3">{C.title}</h3>
      <p className="text-[14px] text-neutral-500 leading-relaxed mb-6" data-testid="custom-set-subtext">{C.subtitle}</p>
      <div className="flex items-center gap-3 mb-7" data-testid="routine-progress">
        <div className="flex-1 h-px bg-neutral-200 relative overflow-hidden">
          <span className="absolute inset-y-0 left-0 bg-riversol-blue transition-[width] duration-500" style={{ width: `${(count / ROUTINE_SLOTS.length) * 100}%` }} />
        </div>
        <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-neutral-600 tabular-nums">
          {complete ? "10% unlocked" : `${count} of ${ROUTINE_SLOTS.length} selected`}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:gap-5 mb-8" data-testid="custom-set-grid">
        {ROUTINE_SLOTS.map((slot) => (
          <SlotTile key={slot.id} slot={slot} pick={picks[slot.id]} active={activeSlot === slot.id} onOpen={() => onOpen(slot.id)} onRemove={() => onRemove(slot.id)} />
        ))}
      </div>
      {count > 0 && (
        <div className="flex items-baseline justify-between mb-4" data-testid="custom-set-subtotal">
          <span className="text-[13px] text-neutral-500">{complete ? "Routine total" : "Subtotal"}</span>
          <div className="flex items-baseline gap-2">
            {complete && <span className="text-[12px] text-neutral-400 line-through font-mono tabular-nums">${subtotal.toFixed(2)}</span>}
            <span className="text-[18px] font-medium text-neutral-900 font-mono tabular-nums">${total.toFixed(2)}</span>
          </div>
        </div>
      )}
      <button
        type="button"
        disabled={count === 0}
        data-testid="custom-set-add-to-cart"
        className={`w-full py-4 text-xs tracking-[0.12em] uppercase font-medium transition-colors ${
          count > 0 ? "bg-riversol-blue text-white hover:bg-riversol-blueLight" : "bg-[#9DB3B3] text-white cursor-not-allowed"
        }`}
      >
        {count > 0 ? `${C.cta} — $${total.toFixed(2)}` : C.cta}
      </button>
    </div>
  );
};

const ProductRow = ({ slot, p, idx, pick, variantLabel, onVariant, onSelect }) => {
  const variant = pickVariant(p, variantLabel);
  const soldOut = p.soldOut || !!variant?.soldOut;
  const price = variant?.price || p.price;
  const active = pick?.productIdx === idx;
  return (
    <div
      data-testid={`bundle-row-${slot.id}-${idx}`}
      data-active={active}
      className={`relative flex gap-5 lg:gap-6 items-center bg-white border p-4 lg:p-5 transition-colors duration-300 ${active ? "border-riversol-blue" : "border-neutral-200 hover:border-neutral-400"}`}
    >
      <span aria-hidden="true" className={`absolute left-0 top-0 bottom-0 w-1 bg-riversol-blue transition-opacity ${active ? "opacity-100" : "opacity-0"}`} />
      <div className="w-24 h-24 lg:w-32 lg:h-32 shrink-0 bg-white border border-neutral-200 overflow-hidden" style={{ borderRadius: "5px" }}>
        <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        {p.badge && <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-neutral-500 mb-1">{p.badge}</span>}
        <h3 className="text-[17px] lg:text-lg text-neutral-900 leading-snug mb-1">{p.name}</h3>
        <p className="text-[13px] text-neutral-500 leading-relaxed max-w-lg">{p.reason}</p>
        <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-neutral-100 flex-wrap">
          <span className="font-mono text-sm text-neutral-900 flex items-baseline gap-2" data-testid={`bundle-row-price-${slot.id}-${idx}`}>
            {price}
            {p.compareAt && !variant && <span className="text-[11px] text-neutral-400 line-through">{p.compareAt}</span>}
          </span>
          <div className="flex items-center gap-2.5">
            {p.variants && <VariantPill variants={p.variants} value={variant.label} onChange={onVariant} testId={`bundle-variant-${slot.id}-${idx}`} />}
            <button
              type="button"
              onClick={onSelect}
              disabled={soldOut}
              aria-pressed={active}
              data-testid={`bundle-toggle-${slot.id}-${idx}`}
              className={`inline-flex items-center justify-center gap-2 font-mono text-[11.5px] tracking-[0.14em] uppercase font-medium transition-all rounded-full min-w-[128px] px-5 py-2.5 ${
                soldOut ? "border border-neutral-200 text-neutral-400 cursor-not-allowed"
                : active ? "bg-riversol-blue text-white border border-riversol-blue"
                : "border border-neutral-300 text-neutral-800 hover:border-riversol-blue hover:text-riversol-blue"
              }`}
            >
              {soldOut ? "Sold out" : active ? <><Check size={14} strokeWidth={2.5} />Selected</> : <><Plus size={14} strokeWidth={2.25} />Select</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RoutineBuilder = () => {
  useProduct();
  const [activeSlot, setActiveSlot] = useState(ROUTINE_SLOTS[0].id);
  const [picks, setPicks] = useState({});
  const [variantSel, setVariantSel] = useState({});
  const listRef = useRef(null);
  const slot = useMemo(() => ROUTINE_SLOTS.find((s) => s.id === activeSlot), [activeSlot]);

  const openSlot = (id) => {
    setActiveSlot(id);
    const top = listRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 80 || top > window.innerHeight * 0.6) listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const select = (s, p, idx) =>
    setPicks((prev) => {
      if (prev[s.id]?.productIdx === idx) {
        const { [s.id]: _omit, ...rest } = prev;
        return rest;
      }
      const variant = pickVariant(p, variantSel[`${s.id}-${idx}`]);
      return { ...prev, [s.id]: { productIdx: idx, product: p, variant, price: variant?.price || p.price } };
    });
  const remove = (id) => setPicks((prev) => { const { [id]: _omit, ...rest } = prev; return rest; });
  const changeVariant = (s, p, idx, label) => {
    setVariantSel((prev) => ({ ...prev, [`${s.id}-${idx}`]: label }));
    const v = p.variants.find((x) => x.label === label);
    setPicks((prev) => {
      if (prev[s.id]?.productIdx !== idx) return prev;
      if (v.soldOut) { const { [s.id]: _omit, ...rest } = prev; return rest; }
      return { ...prev, [s.id]: { ...prev[s.id], variant: v, price: v.price || p.price } };
    });
  };

  return (
    <section className="bg-white py-32" data-testid="complete-routine">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <RoutinePanel picks={picks} activeSlot={activeSlot} onOpen={openSlot} onRemove={remove} />
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col scroll-mt-24" ref={listRef}>
            <div role="tablist" aria-label="Routine steps" className="sticky top-[48px] z-20 bg-white/95 backdrop-blur-sm flex gap-2 lg:gap-3 border-b border-neutral-200 mb-6 -mx-4 px-4 lg:mx-0 lg:px-0 overflow-x-auto hide-scrollbar" data-testid="routine-tabs">
              {ROUTINE_SLOTS.map((s) => {
                const isActive = s.id === activeSlot;
                const done = !!picks[s.id];
                return (
                  <button
                    key={s.id}
                    role="tab"
                    type="button"
                    aria-selected={isActive}
                    data-state={isActive ? "active" : "inactive"}
                    data-done={done}
                    data-testid={`routine-tab-${s.id}`}
                    onClick={() => setActiveSlot(s.id)}
                    className={`relative shrink-0 flex items-center gap-2.5 py-3.5 px-3 lg:px-4 -mb-px border-b-2 font-mono text-[12px] lg:text-[13px] tracking-[0.12em] uppercase transition-colors ${
                      isActive ? "text-neutral-900 border-riversol-blue" : "text-neutral-500 border-transparent hover:text-neutral-900"
                    }`}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] tracking-normal transition-colors ${
                        done ? "bg-riversol-blue text-white" : isActive ? "border border-riversol-blue text-riversol-blue" : "border border-neutral-300 text-neutral-500"
                      }`}
                    >
                      {done ? <Check size={12} strokeWidth={3} /> : s.n}
                    </span>
                    {s.title}
                  </button>
                );
              })}
            </div>
            <div className="mb-4 px-1 flex items-baseline justify-between">
              <div className="text-[13px] text-neutral-600">
                Choose one <span className="text-neutral-900 font-medium">{slot.title.toLowerCase()}</span> for step {slot.n}
              </div>
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400">{slot.products.length} products</div>
            </div>
            <div className="space-y-4" data-testid={`routine-list-${slot.id}`}>
              {slot.products.map((p, idx) => (
                <ProductRow
                  key={p.name}
                  slot={slot}
                  p={p}
                  idx={idx}
                  pick={picks[slot.id]}
                  variantLabel={variantSel[`${slot.id}-${idx}`]}
                  onVariant={(label) => changeVariant(slot, p, idx, label)}
                  onSelect={() => select(slot, p, idx)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoutineBuilder;
